import { React, useState } from "react";
import styled from "styled-components";
import tramData from "../../assets/data/manchester.json";
import { findRoute } from "../../assets/libs/routeFinder";
import { useRoutePlanner } from "../../context/RoutePlannerContext";
import StationAutocomplete from "./StationAutocomplete";

const stations = Object.keys(tramData.stations)
    .filter((key) => !/\s\d+$/.test(key))
    .map((key) => ({
        key,
        label: (tramData.stations[key].label || key).replace(/\n/g, " "),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const OpenButton = styled.button`
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 12px;
    background: #ffffff;
    border: 1px solid #e6e6e6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    font-family: 'Helvetica Neue', Helvetica, Arial, Sans-Serif;
    font-size: 14px;
    font-weight: 600;
    color: #0d0c1d;

    &:hover {
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
    }
`

const Panel = styled.div`
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 10;
    width: 260px;
    padding: 16px;
    border-radius: 14px;
    background: #ffffff;
    border: 1px solid #e6e6e6;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (max-width: 576px) {
        top: auto;
        bottom: 16px;
        right: 16px;
        left: 16px;
        width: auto;
    }
`

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`

const Title = styled.div`
    font-family: 'Helvetica Neue', Helvetica, Arial, Sans-Serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #8a8a8a;
`

const CloseBtn = styled.button`
    border: none;
    background: transparent;
    color: #8a8a8a;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 6px;

    &:hover {
        color: #0d0c1d;
    }
`

const Field = styled.label`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #4d4d4d;
`

const Error = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #c41306;
`

const Summary = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`

const LineDot = styled.span`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ color }) => color};
`

const Buttons = styled.div`
    display: flex;
    gap: 8px;
`

const Button = styled.button`
    flex: 1;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #e6e6e6;
    background: #ffffff;
    font-family: 'Helvetica Neue', Helvetica, Arial, Sans-Serif;
    font-size: 14px;
    font-weight: 600;
    color: #0d0c1d;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
        background: #f5f5f5;
    }

    ${({ primary }) =>
        primary &&
        `
        border: none;
        background: #fc3a34;
        color: #fff;

        &:hover {
            background: #d92a24;
        }
    `}
`

const RouteIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="6" cy="19" r="2.5" />
        <circle cx="18" cy="5" r="2.5" />
        <path d="M8.5 19H11a5 5 0 0 0 5-5V8.5" />
    </svg>
)

const RoutePlanner = ({ mapInstance }) => {
    const { isOpen, open, close } = useRoutePlanner();
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [error, setError] = useState(null);
    const [segments, setSegments] = useState(null);

    const find = () => {
        if (!from) {
            setError("Please choose a starting station.");
            return;
        }
        if (!to) {
            setError("Please choose a destination.");
            return;
        }
        if (from === to) {
            setError("Start and destination must be different.");
            return;
        }

        const route = findRoute(tramData, from, to);

        if (!route) {
            setError("Could not find a route between those stations.");
            setSegments(null);
            mapInstance?.clearRoute();
            return;
        }

        setError(null);
        setSegments(route.segments);
        mapInstance?.drawRoute(route.segments);
    };

    const reset = () => {
        setFrom("");
        setTo("");
        setError(null);
        setSegments(null);
        mapInstance?.clearRoute();
    };

    const handleClose = () => {
        reset();
        close();
    };

    if (!isOpen) {
        return (
            <OpenButton
                type="button"
                onClick={open}
                data-umami-event="Route planner opened"
            >
                <RouteIcon />
                Route
            </OpenButton>
        );
    }

    return (
        <Panel>
            <PanelHeader>
                <Title>Route planner</Title>
                <CloseBtn type="button" onClick={handleClose} aria-label="Close">
                    ×
                </CloseBtn>
            </PanelHeader>
            <Field>
                From
                <StationAutocomplete
                    stations={stations}
                    value={from}
                    onChange={setFrom}
                    placeholder="Select station…"
                />
            </Field>
            <Field>
                To
                <StationAutocomplete
                    stations={stations}
                    value={to}
                    onChange={setTo}
                    placeholder="Select station…"
                />
            </Field>
            {error && <Error>{error}</Error>}
            {segments && (
                <Summary>
                    {segments.map((s, i) => (
                        <LineDot key={i} color={s.color} />
                    ))}
                </Summary>
            )}
            <Buttons>
                <Button
                    primary
                    type="button"
                    onClick={find}
                    data-umami-event="Route search submitted"
                >
                    Find route
                </Button>
                <Button
                    type="button"
                    onClick={reset}
                    data-umami-event="Route search reset"
                >
                    Reset
                </Button>
            </Buttons>
        </Panel>
    );
};

export default RoutePlanner;
